// Web Navigation Hooks - v2.0 (Platform check FIRST)
// This module provides React Navigation compatible hooks for web
import { useNavigation as useReactNavigation, useRoute as useReactRoute } from '@react-navigation/native'
import { Platform } from 'react-native'
import { getData } from '../components/appBar/context/HeaderContext'

// Web history management (singleton init)
let __webHistoryInitialized = false

const initWebHistory = () => {
    if (__webHistoryInitialized) return
    if (typeof window === 'undefined') return
    __webHistoryInitialized = true
    try {
        // Ensure we have an initial history state to intercept browser back
        if (!window.history.state || !window.history.state.__dotsApp) {
            window.history.replaceState({ __dotsApp: true, route: 'INIT' }, '')
        }

        window.addEventListener('popstate', (event) => {
            try {
                const { setCurrentFooterRoute, prevFooterRoute, isBottomSheetOpen, isRightSidebarOpen, goBackInternal } = getData?.() || {}
                // Si hay overlays/widgets abiertos, ignorar el back del navegador
                if (isBottomSheetOpen || isRightSidebarOpen) {
                    // Reponer estado para no salir
                    try { window.history.pushState({ __dotsApp: true, route: 'OVERLAY' }, '') } catch {}
                    return
                }
                // Usar back interno para retroceder una pantalla
                try { goBackInternal && goBackInternal() } catch {}
                // Push a state back so further browser-backs stay within the app
                const { footerRoute: current } = getData()
                window.history.pushState({ __dotsApp: true, route: current || 'LearningHome' }, '')
            } catch (e) {
                // As a fallback, always push a state back to avoid leaving abruptly
                try { window.history.pushState({ __dotsApp: true, route: 'LearningHome' }, '') } catch {}
            }
        })
    } catch (e) {
        // no-op
    }
}

/**
 * useRoute hook compatible with web
 * On web, reads from HeaderContext
 * On native, uses React Navigation's useRoute
 * 
 * IMPORTANT: Platform check MUST happen before calling React Navigation hooks
 * This prevents the "Couldn't find a route object" error on web
 */
export const useRoute = () => {
    // console.log('[useRoute] Platform.OS =', Platform.OS)
    
    // Check platform FIRST to avoid calling React Navigation hooks on web
    if (Platform.OS === 'web') {
        const { navigationParams, footerRoute } = getData()
        // console.log('[useRoute][web] footerRoute:', footerRoute, 'params:', navigationParams)
        
        return {
            params: navigationParams || {},
            name: footerRoute,
            key: footerRoute,
        }
    }
    
    // Only call React Navigation hook on native platforms
    const reactRoute = useReactRoute()
    return reactRoute
}

/**
 * useNavigation hook compatible with web
 * On web, uses HeaderContext to manage navigation
 * On native, uses React Navigation's useNavigation
 * 
 * IMPORTANT: Platform check MUST happen before calling React Navigation hooks
 * This prevents the "Couldn't find a route object" error on web
 */
export const useNavigation = () => {
    // Check platform FIRST to avoid calling React Navigation hooks on web
    if (Platform.OS === 'web') {
    const { setCurrentFooterRoute, footerRoute, goBackInternal } = getData()
        // Initialize web history trap once
        initWebHistory()
        
        return {
            navigate: (routeName, params) => {
                // console.log('[useNavigation][web] navigate to:', routeName, 'params:', params)
                setCurrentFooterRoute(routeName, params)
                try { window.history.pushState({ __dotsApp: true, route: routeName }, '') } catch {}
            },
            reset: ({ index, routes }) => {
                try {
                    const last = routes && routes.length ? routes[routes.length - 1] : { name: 'LearningHome', params: {} }
                    console.log('[useNavigation][web] reset to:', last)
                    setCurrentFooterRoute(last.name, last.params)
                    try { window.history.pushState({ __dotsApp: true, route: last.name }, '') } catch {}
                } catch (e) {
                    console.warn('[useNavigation][web] reset error:', e)
                }
            },
            goBack: () => {
                console.log('[useNavigation][web] goBack - using internal stack')
                try {
                    goBackInternal()
                    try { window.history.pushState({ __dotsApp: true, route: footerRoute }, '') } catch {}
                } catch (e) {
                    // fallback
                    setCurrentFooterRoute('LearningHome', {})
                    try { window.history.pushState({ __dotsApp: true, route: 'LearningHome' }, '') } catch {}
                }
            },
            canGoBack: () => footerRoute !== 'PostsList',
            getState: () => ({
                routes: [{ name: footerRoute }],
                index: 0,
            }),
            isFocused: () => true,
            addListener: (eventName, callback) => {
                // console.log('[useNavigation][web] addListener:', eventName)
                // For web, we DON'T call the callback immediately to avoid infinite loops
                // The component should handle initial data fetch in its own useEffect
                // Return unsubscribe function
                return () => {
                    // console.log('[useNavigation][web] unsubscribe from:', eventName)
                }
            },
            removeListener: (eventName, callback) => {
                console.log('[useNavigation][web] removeListener:', eventName)
            },
        }
    }
    
    // Only call React Navigation hook on native platforms
    const reactNavigation = useReactNavigation()
    return reactNavigation
}
